// styles
import {
  useSoFormStepsApi,
  useSoFormStepsState,
} from 'refactored/features/soForm/SoForm.context';
import styled, { css } from 'styled-components';
import SoFormCandidateBlockSummaryPrice from './SoFormCandidateBlockSummaryPrice';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectors as shipmentsSelectors } from 'state/shipments';

const SoFormCandidateBlockContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  width: 100%;
  cursor: pointer;
`;

const SoFormCandidateBlockLeftFrame = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 4px 4px 4px 16px;
  gap: 30px;
  width: 130px;
  height: 100px;
  background: var(--primary500);
  border-bottom: 4px solid var(--primary300);
  border-radius: 4px 0px 0px 4px;
  ${(props) =>
    !!props.isSelected &&
    css`
      background: var(--primary300);
    `};
`;

const SoFormCandidateBlockLeftFrameInternal = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0px;
`;

const SoFormCandidateBlockLeftFrameInternalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px 0px 4px;
`;

const SoFormCandidateBlockScopeText = styled.div`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  color: var(--primary200);
`;
const SoFormCandidateBlockProductNameText = styled.div`
  max-width: 110px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: var(--white);
`;

const SoFormCandidateBlockRightFrame = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-center;
  padding: 0px;
  height: 100px;
  background: var(--white);
  border-radius: 0px 4px 4px 0px;
  flex-grow: 1;
  border-bottom: 4px solid transparent;
  ${(props) =>
    !!props.isSelected &&
    css`
      border-bottom: 4px solid var(--primary300);
    `};
`;

const SoFormCandidateBlockRightFrameInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 6px 16px 0px;
  gap: 30px;
  height: 80px;
`;

const SoFormCandidateBlockRightFrameInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  height: 44px;
`;

const SoFormCandidateBlockLabelText = styled.div`
  height: 16px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--grey700);
`;

const SoFormCandidateBlockDescriptionText = styled.div`
  height: 24px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: var(--grey700);
`;

// Catalog translations live under price.<key>.* for each product type
const TRANSLATION_KEY_BY_PRODUCT_TYPE = {
  EXPRESS: 'express',
  STANDARD: 'standard',
  ECONOMY: 'economy',
  PALLET_NETWORK: 'pallet_network',
};

// FLEX and EXPRESS can run on long haul (LH) routes, shown as national scope.
// On short haul (SH) they keep the regional "Ontruck" scope, like ECONOMY.
// PALLET_NETWORK is always national.
const PRODUCTS_WITH_NATIONAL_SCOPE_ON_LH = ['STANDARD', 'EXPRESS'];

export const getScopeKey = (productType, businessType) =>
  PRODUCTS_WITH_NATIONAL_SCOPE_ON_LH.includes(productType) &&
  businessType === 'LH'
    ? 'scope_national'
    : 'scope';

// scope (e.g. "Ontruck", "Nacional") on top, product name below
const getNamesForProductType = (productType, businessType, catalogT) => {
  const translationKey = TRANSLATION_KEY_BY_PRODUCT_TYPE[productType];
  if (!translationKey) return { scope: 'Ontruck', name: productType };
  const scopeKey = getScopeKey(productType, businessType);
  return {
    scope: catalogT(`price.${translationKey}.${scopeKey}`),
    name: catalogT(`price.${translationKey}.name`),
  };
};

const getTextsForProductType = (productType, catalogT) => {
  if (productType === 'EXPRESS')
    return {
      label: catalogT('price.express.pickup_and_dropoff_today'),
      description: catalogT(
        'price.express.as_soon_as_possible_even_less_than_3_hours'
      ),
    };
  else if (productType === 'STANDARD') {
    return {
      label: catalogT('price.standard.pickup_and_dropoff_staring_tomorrow'),
      description: catalogT('price.standard.at_the_hour_you_choose'),
    };
  } else if (productType === 'PALLET_NETWORK') {
    return {
      label: catalogT(
        'price.pallet_network.pickup_and_dropoff_staring_tomorrow'
      ),
      description: catalogT('price.pallet_network.at_the_hour_you_choose'),
    };
  } else {
    return {
      label: catalogT('price.economy.pickup_and_dropoff_staring_tomorrow'),
      description: catalogT(
        'price.economy.min_6_hours_between_pickup_and_dropoff'
      ),
    };
  }
};

const SoFormCandidateBlockSummary = ({ candidate, isSelected }) => {
  const stepsApi = useSoFormStepsApi();
  const stepsState = useSoFormStepsState();
  const { t: catalogT } = useTranslation('Catalog');
  const businessType = useSelector(
    (state) =>
      shipmentsSelectors.getShipment(state, 'FIRST_SHIPMENT').route
        ?.business_type
  );

  const isZipcode = stepsState.configuration.isZipcode;
  const { label, description } = getTextsForProductType(
    candidate.productType,
    catalogT
  );

  const { scope, name: productName } = getNamesForProductType(
    candidate.productType,
    businessType,
    catalogT
  );

  return (
    <SoFormCandidateBlockContainer
      onClick={() => {
        if (isZipcode) {
          stepsApi.setShowZipcodeError();
        } else {
          stepsApi.selectProduct(candidate);
        }
      }}
    >
      <SoFormCandidateBlockLeftFrame isSelected={isSelected}>
        <SoFormCandidateBlockLeftFrameInternal>
          <SoFormCandidateBlockLeftFrameInternalContent>
            <SoFormCandidateBlockScopeText>
              {scope}
            </SoFormCandidateBlockScopeText>
            <SoFormCandidateBlockProductNameText>
              {productName}
            </SoFormCandidateBlockProductNameText>
          </SoFormCandidateBlockLeftFrameInternalContent>
        </SoFormCandidateBlockLeftFrameInternal>
      </SoFormCandidateBlockLeftFrame>
      <SoFormCandidateBlockRightFrame isSelected={isSelected}>
        <SoFormCandidateBlockRightFrameInfo>
          <SoFormCandidateBlockRightFrameInfoContent>
            <SoFormCandidateBlockLabelText>
              {label}
            </SoFormCandidateBlockLabelText>
            <SoFormCandidateBlockDescriptionText>
              {description}
            </SoFormCandidateBlockDescriptionText>
          </SoFormCandidateBlockRightFrameInfoContent>
        </SoFormCandidateBlockRightFrameInfo>
        <SoFormCandidateBlockSummaryPrice candidate={candidate} />
      </SoFormCandidateBlockRightFrame>
    </SoFormCandidateBlockContainer>
  );
};

export default styled(SoFormCandidateBlockSummary)``;
